"use client";

import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { getAuthToken } from '../../../utils/authStorage';
import { API_BASE_URL as API_BASE } from '../../../config/api';
import { useTheme } from '../../../contexts/ThemeContext';
import { FiMessageSquare, FiList, FiPlayCircle, FiSave, FiArrowLeft, FiHelpCircle, FiStopCircle, FiImage, FiGlobe, FiGitBranch, FiUser } from 'react-icons/fi';

// --- Custom Nodes ---

const TriggerNode = ({ data }) => {
  const { isDark } = useTheme();
  return (
    <div className={`px-4 py-3 shadow-lg rounded-xl border-2 ${isDark ? 'bg-gray-800 border-purple-500 text-white' : 'bg-white border-purple-500 text-gray-900'}`}>
      <div className="flex items-center gap-2 font-bold mb-2 text-purple-500">
        <FiPlayCircle /> Trigger Node
      </div>
      <div className="text-sm">
        <div className="font-semibold">{data.triggerType}</div>
        <div className={`mt-1 p-1 rounded font-mono text-xs ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>"{data.triggerText}"</div>
      </div>
      <Handle type="source" position={Position.Right} className="w-3 h-3 bg-purple-500" />
    </div>
  );
};

const MessageNode = ({ data }) => {
  const { isDark } = useTheme();
  return (
    <div className={`px-4 py-3 shadow-lg rounded-xl border-2 w-64 ${isDark ? 'bg-gray-800 border-blue-500 text-white' : 'bg-white border-blue-500 text-gray-900'}`}>
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-blue-500" />
      <div className="flex items-center gap-2 font-bold mb-2 text-blue-500">
        <FiMessageSquare /> Send Message
      </div>
      <div className={`text-xs p-2 rounded max-h-24 overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
        {data.contentType === 'template' ? `Template: ${data.templateName}` : data.message || '(Empty Message)'}
      </div>
      {data.mediaType && data.mediaType !== 'none' && (
        <div className="text-[10px] font-bold text-gray-500 mt-2 flex items-center gap-1">
          📎 Media attached ({data.mediaType})
        </div>
      )}
      {data.nextStepMap && data.nextStepMap.length > 0 ? (
        <div className="mt-2 space-y-1">
          {data.nextStepMap.map((map, i) => (
            <div key={i} className={`relative p-1 px-2 rounded text-[10px] font-semibold ${isDark ? 'bg-gray-900 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
              Button: {map.keyword}
              <Handle 
                type="source" 
                position={Position.Right} 
                id={map.keyword}
                className="w-2.5 h-2.5 bg-blue-500 right-[-4px]" 
              />
            </div>
          ))}
        </div>
      ) : (
        <Handle type="source" position={Position.Right} className="w-3 h-3 bg-blue-500" />
      )}
    </div>
  );
};

const MenuNode = ({ data }) => {
  const { isDark } = useTheme();
  return (
    <div className={`px-4 py-3 shadow-lg rounded-xl border-2 w-64 ${isDark ? 'bg-gray-800 border-emerald-500 text-white' : 'bg-white border-emerald-500 text-gray-900'}`}>
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-emerald-500" />
      <div className="flex items-center gap-2 font-bold mb-2 text-emerald-500">
        <FiList /> Menu Options
      </div>
      <div className={`text-xs p-2 rounded mb-2 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
        {data.message || 'Please choose an option:'}
      </div>
      {data.mediaType && data.mediaType !== 'none' && (
        <div className="text-[10px] font-bold text-gray-500 mb-2 flex items-center gap-1">
          📎 Media attached
        </div>
      )}
      <div className="space-y-1">
        {(data.options || []).map((opt, i) => (
          <div key={i} className={`relative p-1 px-2 rounded text-xs font-semibold ${isDark ? 'bg-gray-900' : 'bg-gray-200'}`}>
            {opt.label} ({opt.value})
            <Handle 
              type="source" 
              position={Position.Right} 
              id={opt.value}
              className="w-2 h-2 bg-emerald-500" 
              style={{ top: '50%', right: '-12px' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const QuestionNode = ({ data }) => {
  const { isDark } = useTheme();
  return (
    <div className={`px-4 py-3 shadow-lg rounded-xl border-2 w-64 ${isDark ? 'bg-gray-800 border-amber-500 text-white' : 'bg-white border-amber-500 text-gray-900'}`}>
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-amber-500" />
      <div className="flex items-center gap-2 font-bold mb-2 text-amber-500">
        <FiHelpCircle /> Ask Question
      </div>
      <div className={`text-xs p-2 rounded max-h-24 overflow-hidden mb-2 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
        {data.message || '(Empty Question)'}
      </div>
      {data.saveAs && (
        <div className="text-[10px] font-bold text-gray-500 mb-1">
          Saves answer as: <span className="text-amber-500">{data.saveAs}</span>
        </div>
      )}
      <Handle type="source" position={Position.Right} className="w-3 h-3 bg-amber-500" />
    </div>
  );
};

const EndNode = ({ data }) => {
  const { isDark } = useTheme();
  return (
    <div className={`px-4 py-3 shadow-lg rounded-xl border-2 w-48 ${isDark ? 'bg-gray-800 border-red-500 text-white' : 'bg-white border-red-500 text-gray-900'}`}>
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-red-500" />
      <div className="flex items-center gap-2 font-bold text-red-500">
        <FiStopCircle /> End Flow
      </div>
    </div>
  );
};

const ApiNode = ({ data }) => {
  const { isDark } = useTheme();
  return (
    <div className={`px-4 py-3 shadow-lg rounded-xl border-2 w-64 ${isDark ? 'bg-gray-800 border-fuchsia-500 text-white' : 'bg-white border-fuchsia-500 text-gray-900'}`}>
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-fuchsia-500" />
      <div className="flex items-center gap-2 font-bold text-fuchsia-500 mb-2">
        <FiGlobe /> API Webhook
      </div>
      <div className={`text-[10px] p-2 rounded max-h-24 overflow-hidden mb-1 font-mono break-all ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
        <span className="font-bold text-fuchsia-400 mr-1">{data.apiMethod || 'GET'}</span>
        {data.apiUrl || 'No URL configured'}
      </div>
      <Handle type="source" position={Position.Right} className="w-3 h-3 bg-fuchsia-500" />
    </div>
  );
};

const ConditionNode = ({ data }) => {
  const { isDark } = useTheme();
  return (
    <div className={`px-4 py-3 shadow-lg rounded-xl border-2 w-64 ${isDark ? 'bg-gray-800 border-orange-500 text-white' : 'bg-white border-orange-500 text-gray-900'}`}>
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-orange-500" />
      <div className="flex items-center gap-2 font-bold text-orange-500 mb-2">
        <FiGitBranch /> Logic Split
      </div>
      <div className={`text-xs p-2 rounded mb-2 ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
        If <span className="font-bold text-orange-500">{data.conditionVariable || '[var]'}</span> {data.conditionOperator?.replace('_', ' ')} <span className="font-bold text-orange-500">{data.conditionValue || '[val]'}</span>
      </div>
      <div className="mt-2 space-y-2 relative">
        <div className={`p-1 px-2 rounded text-[10px] font-bold ${isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
          True
          <Handle type="source" position={Position.Right} id="true" className="w-2.5 h-2.5 bg-emerald-500 right-[-10px]" />
        </div>
        <div className={`p-1 px-2 rounded text-[10px] font-bold ${isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600'}`}>
          False
          <Handle type="source" position={Position.Right} id="false" className="w-2.5 h-2.5 bg-red-500 right-[-10px]" />
        </div>
      </div>
    </div>
  );
};

const AgentNode = ({ data }) => {
  const { isDark } = useTheme();
  return (
    <div className={`px-4 py-3 shadow-lg rounded-xl border-2 w-48 ${isDark ? 'bg-gray-800 border-teal-500 text-white' : 'bg-white border-teal-500 text-gray-900'}`}>
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-teal-500" />
      <div className="flex items-center gap-2 font-bold text-teal-500">
        <FiUser /> Talk to Human
      </div>
    </div>
  );
};

const nodeTypes = {
  trigger: TriggerNode,
  message: MessageNode,
  menu: MenuNode,
  question: QuestionNode,
  end: EndNode,
  api: ApiNode,
  condition: ConditionNode,
  agent: AgentNode,
};

// --- Sidebar Palette ---
const Sidebar = () => {
  const { isDark } = useTheme();
  
  const onDragStart = (event, nodeType, defaults) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/nodeData', JSON.stringify(defaults));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className={`w-64 border-r p-4 flex flex-col gap-3 overflow-y-auto ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
      <h3 className={`font-bold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Block Palette</h3>
      <p className="text-[10px] text-gray-400 mb-2">Drag and drop blocks to the canvas.</p>
      
      <div 
        className={`p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'border-blue-500/50 bg-blue-900/20 hover:bg-blue-900/40' : 'border-blue-200 bg-blue-50 hover:bg-blue-100'}`}
        draggable
        onDragStart={(e) => onDragStart(e, 'message', { contentType: 'text', message: 'Hello!', mediaType: 'none', mediaUrl: '' })}
      >
        <FiMessageSquare className="inline mr-2 text-blue-500"/> <span className="font-semibold text-sm">Text Message</span>
      </div>

      <div 
        className={`p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'border-purple-500/50 bg-purple-900/20 hover:bg-purple-900/40' : 'border-purple-200 bg-purple-50 hover:bg-purple-100'}`}
        draggable
        onDragStart={(e) => onDragStart(e, 'message', { contentType: 'text', message: '', mediaType: 'image', mediaUrl: '' })}
      >
        <FiImage className="inline mr-2 text-purple-500"/> <span className="font-semibold text-sm">Media Message</span>
      </div>

      <div 
        className={`p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'border-indigo-500/50 bg-indigo-900/20 hover:bg-indigo-900/40' : 'border-indigo-200 bg-indigo-50 hover:bg-indigo-100'}`}
        draggable
        onDragStart={(e) => onDragStart(e, 'message', { contentType: 'template', templateName: '', mediaType: 'none', mediaUrl: '' })}
      >
        <FiMessageSquare className="inline mr-2 text-indigo-500"/> <span className="font-semibold text-sm">WhatsApp Template</span>
      </div>

      <div 
        className={`p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'border-emerald-500/50 bg-emerald-900/20 hover:bg-emerald-900/40' : 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100'}`}
        draggable
        onDragStart={(e) => onDragStart(e, 'menu', { message: 'Choose an option', options: [{ label: 'Option 1', value: '1' }], menuStyle: 'button' })}
      >
        <FiList className="inline mr-2 text-emerald-500"/> <span className="font-semibold text-sm">Interactive Menu</span>
      </div>

      <div 
        className={`p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'border-amber-500/50 bg-amber-900/20 hover:bg-amber-900/40' : 'border-amber-200 bg-amber-50 hover:bg-amber-100'}`}
        draggable
        onDragStart={(e) => onDragStart(e, 'question', { message: 'What is your name?', saveAs: 'name' })}
      >
        <FiHelpCircle className="inline mr-2 text-amber-500"/> <span className="font-semibold text-sm">Ask Question</span>
      </div>

      <div 
        className={`p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'border-red-500/50 bg-red-900/20 hover:bg-red-900/40' : 'border-red-200 bg-red-50 hover:bg-red-100'}`}
        draggable
        onDragStart={(e) => onDragStart(e, 'end', {})}
      >
        <FiStopCircle className="inline mr-2 text-red-500"/> <span className="font-semibold text-sm">End Flow</span>
      </div>

      <div className={`mt-2 mb-1 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Advanced Nodes</div>

      <div 
        className={`p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'border-fuchsia-500/50 bg-fuchsia-900/20 hover:bg-fuchsia-900/40' : 'border-fuchsia-200 bg-fuchsia-50 hover:bg-fuchsia-100'}`}
        draggable
        onDragStart={(e) => onDragStart(e, 'api', { apiUrl: '', apiMethod: 'GET', apiHeaders: {}, apiBody: '' })}
      >
        <FiGlobe className="inline mr-2 text-fuchsia-500"/> <span className="font-semibold text-sm">API Webhook</span>
      </div>

      <div 
        className={`p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'border-orange-500/50 bg-orange-900/20 hover:bg-orange-900/40' : 'border-orange-200 bg-orange-50 hover:bg-orange-100'}`}
        draggable
        onDragStart={(e) => onDragStart(e, 'condition', { conditionVariable: '', conditionOperator: 'equals', conditionValue: '' })}
      >
        <FiGitBranch className="inline mr-2 text-orange-500"/> <span className="font-semibold text-sm">Logic Split</span>
      </div>

      <div 
        className={`p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'border-teal-500/50 bg-teal-900/20 hover:bg-teal-900/40' : 'border-teal-200 bg-teal-50 hover:bg-teal-100'}`}
        draggable
        onDragStart={(e) => onDragStart(e, 'agent', { handoffMessage: 'Please wait, connecting you to an agent...' })}
      >
        <FiUser className="inline mr-2 text-teal-500"/> <span className="font-semibold text-sm">Talk to Human</span>
      </div>
    </div>
  );
};

// --- Main Builder ---

const FlowBuilderCanvas = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(false);
  
  const { screenToFlowPosition } = useReactFlow();

  const [flowData, setFlowData] = useState({
    name: 'New Bot Flow',
    description: '',
    isActive: true,
    startTriggerType: 'starts_with',
    startTriggerText: 'hi',
    globalFallbackMessage: 'Sorry, I didn’t understand.'
  });

  const [selectedNode, setSelectedNode] = useState(null);
  const [userTemplates, setUserTemplates] = useState([]);

  const token = getAuthToken();
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    if (id && id !== 'new') {
      loadFlow(id);
    } else {
      // Initialize empty canvas with a trigger node
      setNodes([{
        id: 'trigger',
        type: 'trigger',
        position: { x: 100, y: 250 },
        data: { triggerType: 'starts_with', triggerText: 'hi' },
      }]);
    }
    fetchTemplates();
  }, [id]);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/templates/user`, { headers });
      const data = await res.json();
      if (data.success && data.templates) {
        setUserTemplates(data.templates);
      }
    } catch (err) {
      console.error('Failed to fetch templates', err);
    }
  };

  const loadFlow = async (flowId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/bot-flows/${flowId}`, { headers });
      const data = await res.json();
      if (data.success && data.flow) {
        setFlowData({
          name: data.flow.name,
          description: data.flow.description,
          isActive: data.flow.isActive,
          startTriggerType: data.flow.startTriggerType,
          startTriggerText: data.flow.startTriggerText,
          globalFallbackMessage: data.flow.globalFallbackMessage,
        });
        
        // Parse backend steps into React Flow nodes and edges
        const newNodes = [];
        const newEdges = [];

        // 1. Add Trigger Node
        newNodes.push({
          id: 'trigger',
          type: 'trigger',
          position: { x: 50, y: 250 },
          data: { 
            triggerType: data.flow.startTriggerType, 
            triggerText: data.flow.startTriggerText 
          },
        });

        if (data.flow.firstStepId) {
          newEdges.push({
            id: `edge-trigger-${data.flow.firstStepId}`,
            source: 'trigger',
            target: data.flow.firstStepId,
            animated: true,
          });
        }

        // 2. Add Step Nodes
        (data.flow.steps || []).forEach((step, idx) => {
          let nodeType = 'message';
          
          if (step.contentType === 'template') {
            nodeType = 'message'; // Templates are message blocks
          } else if (step.inputType === 'menu') {
            nodeType = 'menu';
          } else if (step.inputType === 'text') {
            nodeType = 'question';
          } else if (step.inputType === 'end') {
            nodeType = 'end';
          }
          
          newNodes.push({
            id: step.stepId,
            type: nodeType,
            position: { x: 350 + (idx * 300), y: 250 }, // Simple auto-layout
            data: {
              ...step
            }
          });

          // 3. Add Edges from Step
          if (step.nextStepMap && step.nextStepMap.length > 0) {
            step.nextStepMap.forEach(map => {
              if (map.nextStepId) {
                newEdges.push({
                  id: `edge-${step.stepId}-${map.keyword}-${map.nextStepId}`,
                  source: step.stepId,
                  sourceHandle: map.keyword,
                  target: map.nextStepId,
                });
              }
            });
          } else if (step.inputType === 'menu' && step.options) {
            step.options.forEach(opt => {
              if (opt.nextStepId) {
                newEdges.push({
                  id: `edge-${step.stepId}-${opt.value}-${opt.nextStepId}`,
                  source: step.stepId,
                  sourceHandle: opt.value,
                  target: opt.nextStepId,
                });
              }
            });
          } else if (step.nextStepId) {
            newEdges.push({
              id: `edge-${step.stepId}-${step.nextStepId}`,
              source: step.stepId,
              target: step.nextStepId,
            });
          }
        });

        setNodes(newNodes);
        setEdges(newEdges);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  const onNodeClick = (event, node) => {
    setSelectedNode(node);
  };

  const onPaneClick = () => {
    setSelectedNode(null);
  };

  const handleNodeDataChange = (field, value) => {
    if (!selectedNode) return;
    setNodes(nds => nds.map(n => {
      if (n.id === selectedNode.id) {
        n.data = { ...n.data, [field]: value };
        // Sync selected node state
        setSelectedNode({ ...n });
      }
      return n;
    }));
  };

  // --- DnD Handlers ---
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      const dataStr = event.dataTransfer.getData('application/nodeData');

      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      
      let defaultData = {};
      try {
        if (dataStr) defaultData = JSON.parse(dataStr);
      } catch(e) {}

      const newNode = {
        id: `step_${Date.now()}`,
        type,
        position,
        data: defaultData,
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [screenToFlowPosition, setNodes],
  );

  const saveFlow = async () => {
    setLoading(true);
    try {
      // Reconstruct steps from nodes/edges
      const steps = [];
      const triggerEdge = edges.find(e => e.source === 'trigger');
      const firstStepId = triggerEdge ? triggerEdge.target : null;

      if (!firstStepId && nodes.length > 1) {
        alert("Please connect the Trigger node to your first step!");
        setLoading(false);
        return;
      }

      nodes.forEach(n => {
        if (n.type === 'trigger') return;
        
        let inputType = 'text';
        if (n.type === 'menu') inputType = 'menu';
        if (n.type === 'question') inputType = 'text';
        if (n.type === 'end') inputType = 'end';
        if (n.type === 'api') inputType = 'api';
        if (n.type === 'condition') inputType = 'condition';
        if (n.type === 'agent') inputType = 'agent';

        const step = {
          stepId: n.id,
          title: n.data.title || n.id,
          contentType: n.data.contentType || 'text',
          message: n.data.message || '',
          templateName: n.data.templateName || '',
          inputType,
          saveAs: n.data.saveAs || '',
          options: n.data.options || [],
          menuStyle: n.data.menuStyle || 'button',
          mediaType: n.data.mediaType || 'none',
          mediaUrl: n.data.mediaUrl || '',
          nextStepId: '',
          
          // API fields
          apiUrl: n.data.apiUrl || '',
          apiMethod: n.data.apiMethod || 'GET',
          apiBody: n.data.apiBody || '',
          
          // Condition fields
          conditionVariable: n.data.conditionVariable || '',
          conditionOperator: n.data.conditionOperator || 'equals',
          conditionValue: n.data.conditionValue || '',
          conditionTrueStepId: '',
          conditionFalseStepId: '',
          
          // Agent fields
          handoffMessage: n.data.handoffMessage || '',
        };

        // Determine next steps from edges
        const outgoingEdges = edges.filter(e => e.source === n.id);
        
        if (n.type === 'menu') {
          // Map options to nextStepIds based on sourceHandle
          step.options = step.options.map(opt => {
            const edge = outgoingEdges.find(e => e.sourceHandle === opt.value);
            return { ...opt, nextStepId: edge ? edge.target : '' };
          });
        } else if (n.type === 'condition') {
          const trueEdge = outgoingEdges.find(e => e.sourceHandle === 'true');
          const falseEdge = outgoingEdges.find(e => e.sourceHandle === 'false');
          if (trueEdge) step.conditionTrueStepId = trueEdge.target;
          if (falseEdge) step.conditionFalseStepId = falseEdge.target;
        } else if (n.type !== 'end' && n.type !== 'agent') {
          if (step.contentType === 'template' && n.data.nextStepMap) {
            // Map template button routes
            step.nextStepMap = n.data.nextStepMap.map(map => {
              const edge = outgoingEdges.find(e => e.sourceHandle === map.keyword);
              return { ...map, nextStepId: edge ? edge.target : '' };
            });
            // Also capture the main default output if any
            const defaultEdge = outgoingEdges.find(e => !e.sourceHandle);
            if (defaultEdge) step.nextStepId = defaultEdge.target;
          } else {
            // Standard next step
            if (outgoingEdges.length > 0) {
              step.nextStepId = outgoingEdges[0].target;
            }
          }
        }
        
        steps.push(step);
      });

      const payload = {
        ...flowData,
        firstStepId: firstStepId || '',
        steps
      };

      const url = id === 'new' ? `${API_BASE}/api/bot-flows` : `${API_BASE}/api/bot-flows/${id}`;
      const method = id === 'new' ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      
      if (data.success) {
        alert("Flow saved successfully!");
        if (id === 'new') {
          navigate(`/botflow/${data.flow._id}`, { replace: true });
        }
      } else {
        alert("Error saving: " + data.message);
      }
    } catch (e) {
      alert("Error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`h-[calc(100vh-4rem)] flex flex-col ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      
      {/* Top Navbar */}
      <div className={`h-16 px-6 flex items-center justify-between border-b ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/botflow')} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
            <FiArrowLeft size={20} />
          </button>
          <div>
            <input 
              type="text" 
              value={flowData.name}
              onChange={(e) => setFlowData({...flowData, name: e.target.value})}
              className={`font-bold text-lg bg-transparent border-none outline-none focus:ring-0 ${isDark ? 'text-white placeholder-gray-600' : 'text-gray-900 placeholder-gray-400'}`}
              placeholder="Flow Name"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={saveFlow}
            disabled={loading}
            className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5 disabled:opacity-70"
          >
            <FiSave /> {loading ? 'Saving...' : 'Save Canvas'}
          </button>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* Left Palette */}
        <Sidebar />

        {/* Canvas Area */}
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            onNodeClick={onNodeClick}
            onPaneClick={() => setSelectedNode(null)}
            onDragOver={onDragOver}
            onDrop={onDrop}
            fitView
            className="bg-gray-50/50 dark:bg-gray-900"
          >
            <Background color={isDark ? '#374151' : '#cbd5e1'} gap={16} />
            <Controls />
            <MiniMap 
              nodeColor={(n) => {
                if (n.type === 'trigger') return '#8b5cf6';
                if (n.type === 'message') return '#3b82f6';
                if (n.type === 'menu') return '#10b981';
                if (n.type === 'question') return '#f59e0b';
                if (n.type === 'end') return '#ef4444';
                return '#9ca3af';
              }} 
              maskColor={isDark ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
            />
          </ReactFlow>
        </div>

        {/* Sidebar Settings Panel */}
        {selectedNode && (
          <div className={`w-80 border-l p-6 overflow-y-auto ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-lg">Node Settings</h3>
              <span className={`text-xs px-2 py-1 rounded-full font-bold uppercase ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                {selectedNode.type}
              </span>
            </div>

            {selectedNode.type === 'trigger' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Trigger Type</label>
                  <select 
                    value={selectedNode.data.triggerType || 'starts_with'}
                    onChange={(e) => handleNodeDataChange('triggerType', e.target.value)}
                    className={`w-full p-2.5 rounded-lg text-sm border outline-none ${isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-500' : 'bg-gray-50 border-gray-200 focus:border-blue-500'}`}
                  >
                    <option value="starts_with">Starts With</option>
                    <option value="contains">Contains</option>
                    <option value="equals">Equals Exact</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Trigger Text</label>
                  <input 
                    type="text"
                    value={selectedNode.data.triggerText || ''}
                    onChange={(e) => handleNodeDataChange('triggerText', e.target.value)}
                    className={`w-full p-2.5 rounded-lg text-sm border outline-none ${isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-500' : 'bg-gray-50 border-gray-200 focus:border-blue-500'}`}
                    placeholder="e.g. hello, hi"
                  />
                </div>
              </div>
            )}

            {(selectedNode.type === 'message' || selectedNode.type === 'menu' || selectedNode.type === 'question') && (
              <div className="space-y-4">
                
                {selectedNode.type !== 'question' && (
                  <div>
                    <label className="block text-sm font-semibold mb-1">Content Type</label>
                    <select 
                      value={selectedNode.data.contentType || 'text'}
                      onChange={(e) => handleNodeDataChange('contentType', e.target.value)}
                      className={`w-full p-2.5 rounded-lg text-sm border outline-none ${isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-500' : 'bg-gray-50 border-gray-200 focus:border-blue-500'}`}
                    >
                      <option value="text">Standard Text</option>
                      <option value="template">WhatsApp Template</option>
                    </select>
                  </div>
                )}

                {selectedNode.data.contentType === 'template' && selectedNode.type !== 'question' ? (
                  <div className="mb-6 border-t pt-4 border-gray-200 dark:border-gray-700">
                    <div className="mb-4">
                      <label className="block text-sm font-semibold mb-1">Template Name</label>
                      <select
                        className={`w-full p-2 border rounded text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        value={selectedNode.data.templateName || ''}
                        onChange={(e) => handleNodeDataChange('templateName', e.target.value)}
                      >
                        <option value="">Select a template...</option>
                        {userTemplates.map(t => (
                          <option key={t._id || t.templateId || t.name} value={t.name}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                      <label className="block text-sm font-semibold mb-2">Template Button Routes</label>
                      <p className="text-[10px] text-gray-500 mb-3">Add the exact button text here to create routing points.</p>
                      
                      <div className="space-y-2 mb-3">
                        {(selectedNode.data.nextStepMap || []).map((map, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input 
                              type="text"
                              value={map.keyword}
                              onChange={(e) => {
                                const newMap = [...(selectedNode.data.nextStepMap || [])];
                                newMap[idx].keyword = e.target.value;
                                handleNodeDataChange('nextStepMap', newMap);
                              }}
                              placeholder="Button text (e.g. Yes)"
                              className={`flex-1 p-2 rounded text-xs border outline-none ${isDark ? 'bg-gray-900 border-gray-700 focus:border-indigo-500' : 'bg-white border-gray-300 focus:border-indigo-500'}`}
                            />
                            <button 
                              onClick={() => {
                                const newMap = selectedNode.data.nextStepMap.filter((_, i) => i !== idx);
                                handleNodeDataChange('nextStepMap', newMap);
                              }}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      <button 
                        onClick={() => {
                          const newMap = [...(selectedNode.data.nextStepMap || []), { keyword: '', nextStepId: '' }];
                          handleNodeDataChange('nextStepMap', newMap);
                        }}
                        className={`w-full py-1.5 rounded-lg text-[10px] font-semibold border border-dashed transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-gray-300' : 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}
                      >
                        + Add Template Button
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        {selectedNode.type === 'question' ? 'Question Text' : 'Message Text'}
                      </label>
                      <textarea 
                        value={selectedNode.data.message || ''}
                        onChange={(e) => handleNodeDataChange('message', e.target.value)}
                        rows={5}
                        className={`w-full p-2.5 rounded-lg text-sm border outline-none resize-none ${isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-500' : 'bg-gray-50 border-gray-200 focus:border-blue-500'}`}
                        placeholder={selectedNode.type === 'question' ? "What is your email?" : "Type your message here..."}
                      />
                    </div>
                    
                    {selectedNode.type !== 'question' && (
                      <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                        <label className="block text-sm font-semibold mb-2">Media Attachment (Optional)</label>
                        <select 
                          value={selectedNode.data.mediaType || 'none'}
                          onChange={(e) => handleNodeDataChange('mediaType', e.target.value)}
                          className={`w-full p-2 rounded-lg text-sm border outline-none mb-3 ${isDark ? 'bg-gray-900 border-gray-700 focus:border-blue-500' : 'bg-white border-gray-200 focus:border-blue-500'}`}
                        >
                          <option value="none">No Media</option>
                          <option value="image">Image</option>
                          <option value="video">Video</option>
                          <option value="document">Document</option>
                        </select>

                        {selectedNode.data.mediaType && selectedNode.data.mediaType !== 'none' && (
                          <div>
                            <input 
                              type="text"
                              value={selectedNode.data.mediaUrl || ''}
                              onChange={(e) => handleNodeDataChange('mediaUrl', e.target.value)}
                              className={`w-full p-2 rounded-lg text-sm border outline-none ${isDark ? 'bg-gray-900 border-gray-700 focus:border-blue-500' : 'bg-white border-gray-200 focus:border-blue-500'}`}
                              placeholder={`https://example.com/file.${selectedNode.data.mediaType === 'image' ? 'jpg' : 'mp4'}`}
                            />
                            <p className="text-[10px] mt-1 text-gray-500">Must be a direct URL to the media file.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {selectedNode.type === 'question' && (
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-amber-500">Save Answer As (Variable Name)</label>
                    <input 
                      type="text"
                      value={selectedNode.data.saveAs || ''}
                      onChange={(e) => handleNodeDataChange('saveAs', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                      className={`w-full p-2.5 rounded-lg text-sm border outline-none ${isDark ? 'bg-gray-800 border-amber-900 focus:border-amber-500' : 'bg-gray-50 border-amber-200 focus:border-amber-500'}`}
                      placeholder="e.g. email, first_name"
                    />
                    <p className="text-[10px] mt-1 text-gray-500">Only letters, numbers, and underscores allowed.</p>
                  </div>
                )}
              </div>
            )}

            {selectedNode.type === 'menu' && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold">Menu Options</label>
                  <select 
                    value={selectedNode.data.menuStyle || 'button'}
                    onChange={(e) => handleNodeDataChange('menuStyle', e.target.value)}
                    className={`p-1 rounded text-xs border outline-none font-medium ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}
                  >
                    <option value="button">Buttons (Max 3)</option>
                    <option value="list">Text List (1, 2, 3)</option>
                  </select>
                </div>
                
                {selectedNode.data.menuStyle === 'button' && (selectedNode.data.options || []).length >= 3 && (
                  <div className="text-[10px] text-amber-500 mb-2 font-bold bg-amber-500/10 p-1.5 rounded">
                    ⚠️ Meta limits interactive buttons to 3. Switch to 'Text List' if you need more.
                  </div>
                )}
                <div className="space-y-3">
                  {(selectedNode.data.options || []).map((opt, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex gap-2 mb-2">
                        <input 
                          type="text"
                          value={opt.value}
                          onChange={(e) => {
                            const newOpts = [...selectedNode.data.options];
                            newOpts[idx].value = e.target.value;
                            handleNodeDataChange('options', newOpts);
                          }}
                          placeholder="Key (e.g. 1)"
                          className={`w-16 p-1.5 rounded text-xs border outline-none ${isDark ? 'bg-gray-900 border-gray-700 focus:border-emerald-500' : 'bg-white border-gray-300 focus:border-emerald-500'}`}
                        />
                        <input 
                          type="text"
                          value={opt.label}
                          onChange={(e) => {
                            const newOpts = [...selectedNode.data.options];
                            newOpts[idx].label = e.target.value;
                            handleNodeDataChange('options', newOpts);
                          }}
                          placeholder="Label (e.g. Sales)"
                          className={`flex-1 p-1.5 rounded text-xs border outline-none ${isDark ? 'bg-gray-900 border-gray-700 focus:border-emerald-500' : 'bg-white border-gray-300 focus:border-emerald-500'}`}
                        />
                        <button 
                          onClick={() => {
                            const newOpts = selectedNode.data.options.filter((_, i) => i !== idx);
                            handleNodeDataChange('options', newOpts);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  ))}
                  <button 
                    onClick={() => {
                      const newOpts = [...(selectedNode.data.options || []), { value: '', label: '' }];
                      handleNodeDataChange('options', newOpts);
                    }}
                    className={`w-full py-2 rounded-lg text-xs font-semibold border border-dashed transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-gray-300' : 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}
                  >
                    + Add Option
                  </button>
                </div>
              </div>
            )}

            {selectedNode.type === 'api' && (
              <div className="mt-4">
                <label className="block text-sm font-semibold mb-1 text-fuchsia-500">API URL</label>
                <input 
                  type="text"
                  value={selectedNode.data.apiUrl || ''}
                  onChange={(e) => handleNodeDataChange('apiUrl', e.target.value)}
                  className={`w-full p-2.5 rounded-lg text-sm border outline-none mb-3 ${isDark ? 'bg-gray-800 border-gray-700 focus:border-fuchsia-500' : 'bg-gray-50 border-gray-200 focus:border-fuchsia-500'}`}
                  placeholder="https://api.example.com/data"
                />

                <label className="block text-sm font-semibold mb-1 text-fuchsia-500">HTTP Method</label>
                <select 
                  value={selectedNode.data.apiMethod || 'GET'}
                  onChange={(e) => handleNodeDataChange('apiMethod', e.target.value)}
                  className={`w-full p-2.5 rounded-lg text-sm border outline-none mb-3 ${isDark ? 'bg-gray-800 border-gray-700 focus:border-fuchsia-500' : 'bg-gray-50 border-gray-200 focus:border-fuchsia-500'}`}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                </select>

                {selectedNode.data.apiMethod === 'POST' && (
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-fuchsia-500">JSON Body</label>
                    <textarea 
                      value={selectedNode.data.apiBody || ''}
                      onChange={(e) => handleNodeDataChange('apiBody', e.target.value)}
                      className={`w-full p-2.5 rounded-lg text-sm border outline-none font-mono min-h-[100px] ${isDark ? 'bg-gray-800 border-gray-700 focus:border-fuchsia-500' : 'bg-gray-50 border-gray-200 focus:border-fuchsia-500'}`}
                      placeholder='{"key": "value"}'
                    />
                  </div>
                )}
                <p className="text-[10px] mt-1 text-gray-500">Response data will be available in session variables.</p>
              </div>
            )}

            {selectedNode.type === 'condition' && (
              <div className="mt-4">
                <label className="block text-sm font-semibold mb-1 text-orange-500">Variable Name to Check</label>
                <input 
                  type="text"
                  value={selectedNode.data.conditionVariable || ''}
                  onChange={(e) => handleNodeDataChange('conditionVariable', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                  className={`w-full p-2.5 rounded-lg text-sm border outline-none mb-3 ${isDark ? 'bg-gray-800 border-gray-700 focus:border-orange-500' : 'bg-gray-50 border-gray-200 focus:border-orange-500'}`}
                  placeholder="e.g. order_status, first_name"
                />

                <label className="block text-sm font-semibold mb-1 text-orange-500">Operator</label>
                <select 
                  value={selectedNode.data.conditionOperator || 'equals'}
                  onChange={(e) => handleNodeDataChange('conditionOperator', e.target.value)}
                  className={`w-full p-2.5 rounded-lg text-sm border outline-none mb-3 ${isDark ? 'bg-gray-800 border-gray-700 focus:border-orange-500' : 'bg-gray-50 border-gray-200 focus:border-orange-500'}`}
                >
                  <option value="equals">Equals</option>
                  <option value="contains">Contains</option>
                  <option value="greater_than">Greater Than</option>
                  <option value="less_than">Less Than</option>
                </select>

                <label className="block text-sm font-semibold mb-1 text-orange-500">Target Value</label>
                <input 
                  type="text"
                  value={selectedNode.data.conditionValue || ''}
                  onChange={(e) => handleNodeDataChange('conditionValue', e.target.value)}
                  className={`w-full p-2.5 rounded-lg text-sm border outline-none ${isDark ? 'bg-gray-800 border-gray-700 focus:border-orange-500' : 'bg-gray-50 border-gray-200 focus:border-orange-500'}`}
                  placeholder="e.g. Shipped, Yes, 100"
                />
              </div>
            )}

            {selectedNode.type === 'agent' && (
              <div className="mt-4">
                <label className="block text-sm font-semibold mb-1 text-teal-500">Handoff Message</label>
                <textarea 
                  value={selectedNode.data.handoffMessage || ''}
                  onChange={(e) => handleNodeDataChange('handoffMessage', e.target.value)}
                  className={`w-full p-2.5 rounded-lg text-sm border outline-none min-h-[100px] ${isDark ? 'bg-gray-800 border-gray-700 focus:border-teal-500' : 'bg-gray-50 border-gray-200 focus:border-teal-500'}`}
                  placeholder="Please wait while we connect you to an agent..."
                />
                <p className="text-[10px] mt-1 text-gray-500">This message is sent before pausing the bot.</p>
              </div>
            )}

            {selectedNode.type !== 'trigger' && (
              <div className="mt-8">
                <button 
                  onClick={() => {
                    setNodes(nds => nds.filter(n => n.id !== selectedNode.id));
                    setEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
                    setSelectedNode(null);
                  }}
                  className="w-full py-2.5 rounded-lg text-sm font-bold bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 transition-colors"
                >
                  Delete Node
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function BotCanvas() {
  return (
    <ReactFlowProvider>
      <FlowBuilderCanvas />
    </ReactFlowProvider>
  );
}
